import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Text from '../../Text/Text';
import { TwoFactorAuthWrapper, TwoFactorInput } from './TwoFactorAuthentication.styles';

function TwoFactorAuthentication(props) {
  const { errorText, isValid, value, disabled, onChange } = props;

  const inputWrapper = useRef();

  const focusBestInput = () => {
    const el = inputWrapper.current;
    const inputs = [...el.querySelectorAll('input')];
    const emptyInput = inputs.find((input) => input.value === '');
    if (emptyInput) {
      emptyInput.focus();
    }
  };

  const handlePaste = (event) => {
    // Prevent default so we can populate individual inputs
    event.stopPropagation();
    event.preventDefault();

    const clipboardData = event.clipboardData || window.clipboardData;

    const el = inputWrapper.current;
    const inputs = [...el.querySelectorAll('input')];

    const pasteString = clipboardData.getData('text').replace(/\s/g, '');
    const trimmedValues = [...pasteString].filter(Boolean).slice(0, inputs.length);

    // Fire Callback with new value
    onChange(trimmedValues);
    focusBestInput();
  };

  const handleKeyboardInput = (event, targetIndex) => {
    const { which, target } = event;

    const DELETE_KEY = 8;
    const NEXT_KEY = 39;
    const PREVIOUS_KEY = 37;

    /* eslint-disable consistent-return */
    switch (which) {
      case DELETE_KEY: {
        const currentValue = value[targetIndex];
        if (!currentValue && targetIndex > 0) {
          target.previousSibling.focus();
        }
        return value;
      }
      case NEXT_KEY: {
        target.nextSibling.focus();
        return value;
      }
      case PREVIOUS_KEY: {
        target.previousSibling.focus();
        return value;
      }
      default: {
        return value;
      }
    }
    /* eslint-enable consistent-return */
  };

  const handleChange = (event, targetIndex) => {
    const { target } = event;
    const inputVal = target.value;
    const lastChar = inputVal[inputVal.length - 1];
    const newValue = value.map((item, int) => (int === targetIndex ? lastChar : item));
    onChange(newValue);
    focusBestInput();
  };

  return (
    <TwoFactorAuthWrapper>
      <div ref={inputWrapper}>
        <TwoFactorInput
          autoFocus
          className={isValid === false ? 'invalid' : ''}
          maxLength="1"
          onPaste={handlePaste}
          onKeyDown={(e) => {
            handleKeyboardInput(e.nativeEvent, 0);
          }}
          onChange={(e) => {
            handleChange(e.nativeEvent, 0);
          }}
          size="1"
          type="text"
          value={value[0] || ''}
          disabled={disabled}
        />
        <TwoFactorInput
          className={isValid === false ? 'invalid' : ''}
          maxLength="1"
          onPaste={handlePaste}
          onKeyDown={(e) => {
            handleKeyboardInput(e.nativeEvent, 1);
          }}
          onChange={(e) => {
            handleChange(e.nativeEvent, 1);
          }}
          size="1"
          type="text"
          value={value[1] || ''}
          disabled={disabled}
        />
        <TwoFactorInput
          className={isValid === false ? 'invalid' : ''}
          maxLength="1"
          onPaste={handlePaste}
          onKeyDown={(e) => {
            handleKeyboardInput(e.nativeEvent, 2);
          }}
          onChange={(e) => {
            handleChange(e.nativeEvent, 2);
          }}
          size="1"
          type="text"
          value={value[2] || ''}
          disabled={disabled}
        />
        <TwoFactorInput
          className={isValid === false ? 'invalid' : ''}
          maxLength="1"
          onPaste={handlePaste}
          onKeyDown={(e) => {
            handleKeyboardInput(e.nativeEvent, 3);
          }}
          onChange={(e) => {
            handleChange(e.nativeEvent, 3);
          }}
          size="1"
          type="text"
          value={value[3] || ''}
          disabled={disabled}
        />
      </div>
      {isValid === false && (
        <Text.CopyParagraph align="center" color="error" tagName="p">
          {errorText}
        </Text.CopyParagraph>
      )}
    </TwoFactorAuthWrapper>
  );
}

TwoFactorAuthentication.propTypes = {
  /**
   * Error message to user
   */
  errorText: PropTypes.string,
  /**
   * When false updates styles and displays error text to let the user know the PIN they have provided is invalid
   */
  isValid: PropTypes.bool,
  /**
   * Callback function whenever value is updated
   */
  onChange: PropTypes.func,
  /**
   * PIN value
   */
  value: PropTypes.arrayOf(PropTypes.string),
  /**
   * Set enabled/disabled state for submitting a token
   */
  disabled: PropTypes.bool
};

TwoFactorAuthentication.defaultProps = {
  errorText: 'Error: Invalid entry',
  isValid: undefined,
  onChange: undefined,
  value: ['', '', '', ''],
  disabled: false
};

export default TwoFactorAuthentication;
